// Printing Setup settings strings — EN + TR.
//
// Wave-2 Day 4 Task 4.1. Settings sub-section for ShipStation-pattern printing
// configuration (V1 mock; M3 Stage 4 SPEC.md Faz 3 = real PDF generator +
// label rendering + printer integration backend). 1162 LOC source file was
// the largest single file flagged by the i18n audit 2026-06-02.
//
// Scope: page chrome + Document Type table + Print Warnings + Supplies
// upsell + 3 sub-dialogs (Label Layout / Packing Slip / Order Summary) +
// toast messages (MSW errors + backend ratify notes) + non-admin placeholder
// (D-7 surface segregation).
//
// Source: features/settings/PrintingSetup.tsx.

import type { LocalizedDict } from '../index'

type PrintingSetupStrings = {
  pageTitle: string
  pageDescription: string
  browserPrintDescription: string
  browserPrint: string
  helpAriaLabel: string
  placeholder: {
    title: string
    description: (surface: 'Seller' | 'Supply') => string
    mockFooter: string
  }
  states: {
    loading: string
    loadFailedPrefix: string
    loadFailedSuffix: string
  }
  supplies: {
    storeLink: string
    storeHint: string
    needMoreTitle: string
    needMoreHint: string
  }
  columns: {
    documentType: string
    printTo: string
    documentFormat: string
    options: string
    documentOptions: string
  }
  warnings: {
    title: string
    hint: string
    reprintPackingSlipLabel: string
    reprintPackingSlipDescription: string
    reprintLabelsLabel: string
    reprintLabelsDescription: string
  }
  footerNote: string
  dialogs: {
    info: string
    labelLayout: {
      title: string
      sampleLink: string
      sampleCarriers: { usps: string; ups: string }
      sampleDrawing: string
      sampleNote: string
      labelDpiTitle: string
      labelDpiNote: string
      batchSummaryTitle: string
      orderLabelsByTitle: string
      orderLabelsByHint: string
      labelMessagesTitle: string
      labelMessagesHint: string
      messageNumber: (n: number) => string
      messagePlaceholder: string
      messagesNote: string
      tokensLabel: string
      cutoffNote: string
      shippingCutoffTitle: string
      shippingCutoffLabel: string
      sizeNotice: (size: string) => string
      layoutLabelOnly: string
      layoutLabelWithSlip: string
      labelPrinterCategory: string
      preview: string
      saveSettings: string
    }
    packingSlip: {
      title: string
      lead: string
      formatField: string
      sortItemsField: string
      toggles: {
        storeLogoLabel: string
        storeLogoDescription: string
        itemPricesLabel: string
        itemPricesDescription: string
        itemSkusLabel: string
        itemSkusDescription: string
        scanToViewLabel: string
        scanToViewDescription: string
        itemLocationsLabel: string
        itemLocationsDescription: string
        discountsLabel: string
        discountsDescription: string
        zeroQtyLabel: string
        zeroQtyDescription: string
        returnQrLabel: string
        returnQrDescription: string
      }
      preview: string
      previewNote: string
      cancel: string
      saveSettings: string
    }
    orderSummary: {
      title: string
      lead: string
      sortOrdersByField: string
      hideImages: string
      useProductSku: string
      hideDetails: string
      includeScanToView: string
      cancel: string
      saveSettings: string
    }
  }
  toasts: {
    saveFailedTitle: string
    saveFailedDesc: string
    previewFailedTitle: string
    previewFailedDesc: string
    suppliesStoreTitle: string
    suppliesStoreDesc: string
    crossSellDescSuffix: string
    crossSellTitleSuffix: string
    comingSoon: (label: string) => string
    backendRatifyNote: string
    savedTitle: string
    labelLayoutSavedDesc: string
    packingSlipSavedDesc: string
    orderSummarySavedDesc: string
  }
  documentTypes: {
    label: string
    return_label: string
    packing_slip: string
    end_of_day: string
    other_forms: string
    order_summary: string
    pick_list: string
    shipment_manifest: string
    customs_form: string
  }
}

export const printingSetupI18n: LocalizedDict<PrintingSetupStrings> = {
  en: {
    browserPrintDescription: 'Document options apply to the whole account. PDFs open in your browser; choose the printer, paper size and 100% scale in its print dialog.',
    browserPrint: 'Browser print dialog',
    pageTitle: 'Printing Setup',
    pageDescription:
      'Choose how your documents print from PEXIFY. **Document Options** are account-wide settings, but your **Print To** settings will be saved for this machine and browser only.',
    helpAriaLabel: 'Help',
    placeholder: {
      title: 'Printing managed by admin',
      description: (surface) =>
        `Label format, packing slip layout and document routing are PEXIFY-admin owned resources (D-6). ${surface} organizations consume centrally configured templates; per-org overrides live in Stage 4 SPEC.md scope (M3+).`,
      mockFooter:
        'Mock-only V1 · M3 Stage 4 SPEC.md Faz 3 ratifies per-org override surface.',
    },
    states: {
      loading: 'Loading printing setup…',
      loadFailedPrefix: 'Could not load printing setup. Is the MSW handler ',
      loadFailedSuffix: ' running?',
    },
    supplies: {
      storeLink: 'Supplies Store',
      storeHint: 'Never run out of the essentials you need to print and ship.',
      needMoreTitle: 'Need More Labels?',
      needMoreHint:
        'Shop for label sheets and rolls at the PEXIFY Supplies store (mock).',
    },
    columns: {
      documentType: 'Document Type',
      printTo: 'Print To',
      documentFormat: 'Document Format',
      options: 'Options',
      documentOptions: 'Document Options',
    },
    warnings: {
      title: 'Print Warnings Setup',
      hint: 'Print Warnings Settings will be saved for this machine and browser only.',
      reprintPackingSlipLabel: 'Show Reprint Warnings for Packing Slips',
      reprintPackingSlipDescription:
        'Prompts when you re-issue a packing slip for the same order.',
      reprintLabelsLabel: 'Show Reprint Warnings for Labels',
      reprintLabelsDescription:
        'Prompts when you re-issue a shipping label that was already generated.',
    },
    footerNote:
      'Mock-only V1 · M3 Stage 4 SPEC.md Faz 3 (Orders + Shipping print sub-cluster) ratifies real PDF generation + label rendering + printer integration.',
    dialogs: {
      info: 'Adjusting any of the following options affects all users on your account.',
      labelLayout: {
        title: 'Select a Label Layout',
        sampleLink: 'View a sample label',
        sampleCarriers: { usps: 'USPS', ups: 'UPS' },
        sampleDrawing: 'Drawing…',
        sampleNote:
          "PEXIFY's own drawing of the label, with the options on this screen filled in — it shows where your message lines, your logo and the ship date land. The carrier prints the real one, so the artwork is not identical.",
        labelDpiTitle: 'Label DPI',
        labelDpiNote:
          'Asked of BL only (203 or 300). Other carriers send a PDF that prints sharp at any DPI.',
        batchSummaryTitle: 'Batch Summary Page Option',
        orderLabelsByTitle: 'Order Labels By',
        orderLabelsByHint:
          'This setting applies to the print order for both labels and packing slips.',
        labelMessagesTitle: 'Label Messages',
        labelMessagesHint:
          'Define up to 3 custom messages to print on your shipping label, including dynamic information about the order. Each message is limited to 26 characters.',
        messageNumber: (n) => `Message #${n}`,
        messagePlaceholder: 'Order [Order #]',
        messagesNote:
          "Printed on the label, up to 26 characters per line once filled in. ShipStation prints all three lines itself; where the channel prints fewer or none, PEXIFY prints them into the label's own empty foot.",
        tokensLabel: 'Insert a field',
        cutoffNote:
          'Warehouse time (US Eastern). After the cutoff the label carries the next shipping day; Sundays are skipped.',
        shippingCutoffTitle: 'Shipping Cutoff',
        shippingCutoffLabel:
          'Set the Ship Date to the next day if the time is after:',
        sizeNotice: (size) =>
          `${size} layout templates are part of the M3 PDF generator backend scope.`,
        layoutLabelOnly: '4" x 6"',
        layoutLabelWithSlip: '4" x 6" + Packing Slip',
        labelPrinterCategory: 'Label Printer - Zebra, Dymo, etc.',
        preview: 'Preview',
        saveSettings: 'Save Settings',
      },
      packingSlip: {
        title: 'Packing Slip Printing Setup',
        lead: 'Choose how your Packing Slips appear.',
        formatField: 'Packing Slip Format',
        sortItemsField: 'Sort items by',
        toggles: {
          storeLogoLabel: 'Store Logo',
          storeLogoDescription: 'Show store logo',
          itemPricesLabel: 'Item Prices',
          itemPricesDescription: 'Show item prices',
          itemSkusLabel: 'Item SKUs',
          itemSkusDescription: 'Show item SKUs',
          scanToViewLabel: '“Scan to View” Barcode',
          scanToViewDescription: "Show 'Scan to View' barcode",
          itemLocationsLabel: 'Item Locations',
          itemLocationsDescription: 'Show item locations',
          discountsLabel: 'Discounts',
          discountsDescription: 'Show discounts as line items',
          zeroQtyLabel: 'Zero Quantity Items',
          zeroQtyDescription: 'Show 0 quantity items',
          returnQrLabel: 'Return QR Code',
          returnQrDescription:
            'Show QR code to allow customers to start a return',
        },
        preview: 'Preview 4" x 6" slip',
        previewNote:
          'Every packing slip prints 4" x 6" today, whichever format is chosen above; the letter formats are on the way. The preview shows that 4" x 6" slip with the options on this screen.',
        cancel: 'Cancel',
        saveSettings: 'Save Settings',
      },
      orderSummary: {
        title: 'Order Summary Printing Setup',
        lead: 'Choose how your Order Summary appears.',
        sortOrdersByField: 'Sort Orders By',
        hideImages: 'Hide images on the Order Summary Report',
        useProductSku:
          'Use the product SKU rather than the selling channel SKU',
        hideDetails: 'Hide details (Items Only)',
        includeScanToView: "Include 'Scan to View' barcode for each",
        cancel: 'Cancel',
        saveSettings: 'Save Settings',
      },
    },
    toasts: {
      saveFailedTitle: 'Settings were not saved',
      saveFailedDesc: 'Nothing changed. Check your connection and save again.',
      previewFailedTitle: 'Preview could not be drawn',
      previewFailedDesc: 'Your options are untouched. Try the preview again.',
      suppliesStoreTitle: 'Supplies Store (mock)',
      suppliesStoreDesc:
        'Stage 4 SPEC.md Faz 5 cross-sell — partner integration option.',
      crossSellDescSuffix:
        'Supplies store partner integration — Stage 4 SPEC.md Faz 5.',
      crossSellTitleSuffix: '(mock cross-sell)',
      comingSoon: (label) => `${label} options coming soon`,
      backendRatifyNote: 'M3 Stage 4 SPEC.md Faz 3 backend ratification.',
      savedTitle: 'Saved',
      labelLayoutSavedDesc:
        'Label options apply to the next print run.',
      packingSlipSavedDesc:
        'Packing slip options apply to the next slip printed.',
      orderSummarySavedDesc:
        'Order summary options saved.',
    },
    documentTypes: {
      label: 'Label',
      return_label: 'Return Label',
      packing_slip: 'Packing Slip',
      end_of_day: 'End Of Day Form',
      other_forms: 'Other Forms',
      order_summary: 'Order Summary',
      pick_list: 'Pick List',
      shipment_manifest: 'Shipment Manifest',
      customs_form: 'Customs Form',
    },
  },
  tr: {
    browserPrintDescription: 'Belge seçenekleri tüm hesap için geçerlidir. PDF tarayıcıda açılır; yazıcıyı, kağıt boyutunu ve %100 ölçeği tarayıcının yazdırma penceresinde seçin.',
    browserPrint: 'Tarayıcı yazdırma penceresi',
    pageTitle: 'Yazdırma Ayarları',
    pageDescription:
      'Belgelerinin PEXIFY üzerinden nasıl yazdırılacağını seç. **Belge Seçenekleri** hesap geneli ayarlardır, ancak **Yazdırma Hedefi** ayarların yalnızca bu makine ve tarayıcı için saklanır.',
    helpAriaLabel: 'Yardım',
    placeholder: {
      title: 'Yazdırma admin tarafından yönetilir',
      description: (surface) => {
        const tr = surface === 'Seller' ? 'Satıcı' : 'Üretim'
        return `Etiket formatı, packing slip yerleşimi ve belge yönlendirme PEXIFY-admin sahipli kaynaklardır (D-6). ${tr} organizasyonları merkezi olarak yapılandırılmış şablonları tüketir; per-org override'lar Stage 4 SPEC.md kapsamındadır (M3+).`
      },
      mockFooter:
        'Sadece mock V1 · M3 Stage 4 SPEC.md Faz 3 per-org override yüzeyini ratify eder.',
    },
    states: {
      loading: 'Yazdırma ayarları yükleniyor…',
      loadFailedPrefix: 'Yazdırma ayarları verisi yüklenemedi. MSW handler ',
      loadFailedSuffix: ' ayağa kalkmış mı?',
    },
    supplies: {
      storeLink: 'Sarf Mağazası',
      storeHint:
        'Yazdırma ve gönderim için ihtiyaç duyduğun temel malzemeler tükenmesin.',
      needMoreTitle: 'Daha Fazla Etikete mi İhtiyacın Var?',
      needMoreHint:
        'PEXIFY Sarf mağazasından etiket sayfası ve rulo satın al (mock).',
    },
    columns: {
      documentType: 'Belge Tipi',
      printTo: 'Yazdırma Hedefi',
      documentFormat: 'Belge Formatı',
      options: 'Seçenekler',
      documentOptions: 'Belge Seçenekleri',
    },
    warnings: {
      title: 'Yazdırma Uyarıları Kurulumu',
      hint: 'Yazdırma Uyarısı Ayarları yalnızca bu makine ve tarayıcı için saklanır.',
      reprintPackingSlipLabel: 'Packing Slip Tekrar Yazdırma Uyarısı Göster',
      reprintPackingSlipDescription:
        'Aynı sipariş için packing slip yeniden basıldığında uyarı verir.',
      reprintLabelsLabel: 'Etiket Tekrar Yazdırma Uyarısı Göster',
      reprintLabelsDescription:
        'Zaten oluşturulmuş bir gönderi etiketi yeniden basıldığında uyarı verir.',
    },
    footerNote:
      'Sadece mock V1 · M3 Stage 4 SPEC.md Faz 3 (Orders + Shipping print alt-kümesi) gerçek PDF üretimi + etiket rendering + yazıcı entegrasyonunu ratify eder.',
    dialogs: {
      info: 'Aşağıdaki seçeneklerin herhangi birini değiştirmek hesabındaki tüm kullanıcıları etkiler.',
      labelLayout: {
        title: 'Etiket Yerleşimi Seç',
        sampleLink: 'Örnek etiketi gör',
        sampleCarriers: { usps: 'USPS', ups: 'UPS' },
        sampleDrawing: 'Çiziliyor…',
        sampleNote:
          'Etiketin PEXIFY tarafından çizilmiş hali — bu ekrandaki ayarlar doldurulmuş olarak. Mesaj satırlarının, logonun ve sevk tarihinin nereye düştüğünü gösterir. Gerçeğini taşıyıcı bastığı için görsel birebir aynı olmaz.',
        labelDpiTitle: 'Etiket DPI',
        labelDpiNote:
          'Yalnızca BL’den istenir (203 veya 300). Diğer taşıyıcılar her DPI’da net basılan PDF gönderir.',
        batchSummaryTitle: 'Batch Özet Sayfası Seçeneği',
        orderLabelsByTitle: 'Etiketleri Şuna Göre Sırala',
        orderLabelsByHint:
          'Bu ayar hem etiketler hem de packing slip’ler için yazdırma sırasına uygulanır.',
        labelMessagesTitle: 'Etiket Mesajları',
        labelMessagesHint:
          'Gönderi etiketine basılacak 3 adede kadar özel mesaj tanımla — siparişe ait dinamik bilgi de dahil. Her mesaj 26 karakterle sınırlıdır.',
        messageNumber: (n) => `Mesaj #${n}`,
        messagePlaceholder: 'Order [Order #]',
        messagesNote:
          'Etiketin üstüne basılır; alanlar dolduktan sonra satır başına en çok 26 karakter. ShipStation üç satırı da kendi basar; daha azını basan ya da hiç basmayan kanallarda satırları etiketin alt boşluğuna PEXIFY kendisi basar.',
        tokensLabel: 'Alan ekle',
        cutoffNote:
          'Depo saati (ABD Doğu). Kesim saatinden sonra alınan etiket bir sonraki gönderim gününü taşır; pazar günleri atlanır.',
        shippingCutoffTitle: 'Gönderim Kesim Saati',
        shippingCutoffLabel:
          'Saat şu değeri geçtiğinde Ship Date’i ertesi güne ayarla:',
        sizeNotice: (size) =>
          `${size} yerleşim şablonları M3 PDF generator backend kapsamındadır.`,
        layoutLabelOnly: '4" x 6"',
        layoutLabelWithSlip: '4" x 6" + Packing Slip',
        labelPrinterCategory: 'Etiket yazıcısı - Zebra, Dymo vb.',
        preview: 'Önizleme',
        saveSettings: 'Ayarları Kaydet',
      },
      packingSlip: {
        title: 'Packing Slip Yazdırma Kurulumu',
        lead: 'Packing Slip’lerinin nasıl görüneceğini seç.',
        formatField: 'Packing Slip Formatı',
        sortItemsField: 'Ürünleri sırala',
        toggles: {
          storeLogoLabel: 'Mağaza Logosu',
          storeLogoDescription: 'Mağaza logosunu göster',
          itemPricesLabel: 'Ürün Fiyatları',
          itemPricesDescription: 'Ürün fiyatlarını göster',
          itemSkusLabel: 'Ürün SKU’ları',
          itemSkusDescription: 'Ürün SKU’larını göster',
          scanToViewLabel: '“Scan to View” Barkodu',
          scanToViewDescription: '“Scan to View” barkodunu göster',
          itemLocationsLabel: 'Ürün Lokasyonları',
          itemLocationsDescription: 'Ürün lokasyonlarını göster',
          discountsLabel: 'İndirimler',
          discountsDescription: 'İndirimleri satır olarak göster',
          zeroQtyLabel: 'Sıfır Adet Ürünler',
          zeroQtyDescription: 'Adedi 0 olan ürünleri göster',
          returnQrLabel: 'İade QR Kodu',
          returnQrDescription:
            'Müşterinin iade başlatabileceği QR kodu göster',
        },
        preview: '4" x 6" slip önizlemesi',
        previewNote:
          'Bugün her packing slip, yukarıda hangi format seçilirse seçilsin 4" x 6" basılır; letter formatları yolda. Önizleme, bu ekrandaki seçeneklerle o 4" x 6" slibi gösterir.',
        cancel: 'Vazgeç',
        saveSettings: 'Ayarları Kaydet',
      },
      orderSummary: {
        title: 'Sipariş Özeti Yazdırma Kurulumu',
        lead: 'Sipariş Özeti’nin nasıl görüneceğini seç.',
        sortOrdersByField: 'Siparişleri Sırala',
        hideImages: 'Sipariş Özeti Raporunda görselleri gizle',
        useProductSku:
          'Satış kanalı SKU’su yerine ürün SKU’sunu kullan',
        hideDetails: 'Detayları gizle (Sadece Ürünler)',
        includeScanToView: 'Her biri için “Scan to View” barkodu ekle',
        cancel: 'Vazgeç',
        saveSettings: 'Ayarları Kaydet',
      },
    },
    toasts: {
      saveFailedTitle: 'Ayarlar kaydedilmedi',
      saveFailedDesc: 'Hiçbir şey değişmedi. Bağlantını kontrol edip yeniden kaydet.',
      previewFailedTitle: 'Önizleme çizilemedi',
      previewFailedDesc: 'Seçeneklerin yerinde duruyor. Önizlemeyi yeniden dene.',
      suppliesStoreTitle: 'Sarf Mağazası (mock)',
      suppliesStoreDesc:
        'Stage 4 SPEC.md Faz 5 cross-sell — partner entegrasyonu opsiyonu.',
      crossSellDescSuffix:
        'Sarf mağazası partner entegrasyonu — Stage 4 SPEC.md Faz 5.',
      crossSellTitleSuffix: '(mock cross-sell)',
      comingSoon: (label) => `${label} seçenekleri yakında`,
      backendRatifyNote: 'M3 Stage 4 SPEC.md Faz 3 backend ratification.',
      savedTitle: 'Kaydedildi',
      labelLayoutSavedDesc:
        'Etiket seçenekleri bir sonraki basımda geçerli.',
      packingSlipSavedDesc:
        'Packing slip seçenekleri bir sonraki basılan slipte geçerli.',
      orderSummarySavedDesc:
        'Sipariş özeti seçenekleri kaydedildi.',
    },
    documentTypes: {
      label: 'Etiket',
      return_label: 'İade Etiketi',
      packing_slip: 'Packing Slip',
      end_of_day: 'Gün Sonu Formu',
      other_forms: 'Diğer Formlar',
      order_summary: 'Sipariş Özeti',
      pick_list: 'Toplama Listesi',
      shipment_manifest: 'Gönderi Manifestosu',
      customs_form: 'Gümrük Formu',
    },
  },
}
